<template>
  <div :style="fill ? 'position: relative; height: 100%;' : ''">
    <canvas ref="pieChart" :class="{ 'chart-canvas-fill': fill }"></canvas>
  </div>
</template>

<script>
import Chart from 'chart.js';
import currencies from "@/assets/currencies.json";
import {date} from "@/plugins/dates";
import _ from "lodash";

export default {
  name: "PieChart",
  data() {
    return {
      chart: null,
      currency_symbol: '$',
      currency: null,
      currency_format: null,
      number_format: null,
      currencies: currencies.currencies
    };
  },
  props: {
    // Fill the parent's height instead of the fixed 350px (dashboard tiles).
    fill: {
      type: Boolean,
      default: false
    },
    chartData: {
      type: Array,
      required: true,
      default: () => []
    },
    backgroundColors: {
      type: Array,
      default: () => ['#1d8cf8', '#e14eca', '#ff8d72', '#00f2c3', '#fd5d93',
        "#c5fb70", "#49341b", "#3aeae1", "#3012a1", "#f0bbb6", "#ff8d72", "#3d6066", "#32f432",
        "#680e6d", "#b84713", "#77e2b0", "#e56580", "#27ca36", "#109d60", "#42aa70", "#d562f0",
        "#dd7090", "#630a4e", "#3cd234", "#988ef5", "#14b0d4", "#e9557a", "#0a05ed", "#26c54d",
        "#dd4c7c", "#1d0b1c", "#d7aa41", "#431966", "#6642ae", "#67a026", "#858aa0", "#157a0c",
        "#fcef57", "#d75f5a", "#a44401", "#e4eb49", "#9bd97f", "#9c31e3", "#6902b0", "#e00e46",
        "#7d0f0b", "#db192a", "#868c17", "#e3512a", "#97dfa9", "#0387e6", "#6aae42", "#5f75d0",
        "#00f2c3", "#1c6a95", "#7b59fb", "#b54b6e", "#dbe2ad", "#056bbb", "#04c998", "#8ea315",
        "#708777", "#9bfcc2", "#fd5d93", "#0767da", "#ba2be0", "#a24bd0", "#4fc623", "#bce910",
        "#794dde", "#92f6a8", "#d773ee", "#e14eca", "#eb83b9", "#d69f69", "#5f7df0", "#225260",
        "#33da07", "#0e3fb8", "#25ed46", "#bbf5b4", "#1d8cf8", "#576fe8", "#ba3fec", "#3c8e4a",
        "#efe241", "#5a72dd", "#e29981", "#fbe33f", "#901a22", "#0b8752", "#a1e417", "#28bbe9",
        "#0fefae", "#b58bf8", "#7bf97a", "#834fb0", "#511c10", "#b930fa", "#1abf7e", "#c4e9a0",
        "#af409d", "#7c5913", "#7939cb", "#3e0d19"]
    },
    dataFormat: {
      type: String,
      default: 'count'
    }
  },
  watch: {
    chartData: {
      deep: true,
      handler() {
        this.createChart();
      }
    }
  },
  created() {
    this.initializeCurrency();
  },
  mounted() {
    this.createChart();
    if (this.fill) {
      window.addEventListener('dashboard-widget-resize', this.onTileResize);
    }
  },
  computed: {
    filteredData() {
      // Filter out null labels and zero values, then sort by label (numeric-aware so "2 players" precedes "10 players")
      // Sorting ensures consistent color assignment regardless of API response order
      return this.chartData
        .filter(item =>
          item &&
          item.label !== null &&
          item.label !== undefined &&
          item.label !== '' &&
          Number(item.value) > 0
        )
        .sort((a, b) => String(a.label).localeCompare(String(b.label), undefined, { numeric: true }));
    },
    labels() {
      return this.filteredData.map(item => item.label || 'Unknown');
    },
    values() {
      return this.filteredData.map(item => Number(item.value));
    },
  },
  methods: {
    onTileResize() {
      if (this.chart) {
        this.chart.resize();
      }
    },
    initializeCurrency() {
      try {
        const group = JSON.parse(localStorage.getItem('group'));
        this.currency = group?.currency || 'USD';
        this.currency_format = group?.currency_format || 0;
        this.number_format = group?.number_format || 0;

        const currencyData = _.find(this.currencies, { code: this.currency });
        this.currency_symbol = currencyData?.symbol || '$';
      } catch (error) {
        console.warn('Error loading currency settings:', error);
        // Fallback values
        this.currency = 'USD';
        this.currency_format = 0;
        this.number_format = 0;
        this.currency_symbol = '$';
      }
    },

    formatCurrency(amount) {
      if (amount == null || amount === '') return '';

      const value = parseFloat(amount);
      if (isNaN(value)) return amount;

      // Handle SEK and DKK formatting (postfix krone symbols)
      if (this.currency === 'SEK' || this.currency === 'DKK') {
        const formattedAmount = value.toLocaleString(this.currency === 'DKK' ? 'da-DK' : 'sv-SE', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        });
        return `${formattedAmount} ${this.currency === 'DKK' ? 'kr.' : 'kr'}`;
      }

      // Handle other currency formatting based on existing logic
      if (this.number_format === 1) {
        const localizedAmount = value.toLocaleString(undefined, {
          minimumFractionDigits: 2
        }).replace('.', ',');

        if (this.currency_format === 0) {
          return this.currency_symbol + localizedAmount;
        } else {
          return localizedAmount + ' ' + this.currency;
        }
      }

      // Default formatting
      if (this.currency_format === 0) {
        return this.currency_symbol + value.toFixed(2);
      } else {
        return value.toFixed(2) + ' ' + this.currency;
      }
    },

    getValueFormat(data_format, integer) {
      if (integer == null) {
        return '';
      }

      if (data_format !== 'count' && data_format !== 'quantity' && data_format !== 'date') {
        return this.formatCurrency(integer);
      }

      if (data_format === 'date') {
        return date(integer, this.date_format);
      }

      return Number(integer);
    },

    createChart() {
      if (this.chart) {
        this.chart.destroy();
      }

      // Check if we have valid data to render
      if (!this.filteredData || this.filteredData.length === 0) {
        console.warn('No valid data to render pie chart');
        return;
      }

      // Set canvas size explicitly before rendering
      const canvas = this.$refs.pieChart;
      if (!canvas) {
        console.error('Canvas ref not found');
        return;
      }

      if (!this.fill) { canvas.height = 350; }
      canvas.width = 350;

      const ctx = canvas.getContext('2d');
      this.chart = new Chart(ctx, {
        type: 'pie',
        data: {
          labels: this.labels,
          datasets: [
            {
              data: this.values,
              backgroundColor: this.backgroundColors.slice(0, this.labels.length),
              borderColor: '#ffffff',
              borderWidth: 2
            }
          ]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          legend: {
            display: true,
            position: 'bottom',
            labels: {
              boxWidth: 15,
              boxHeight: 5,
              padding: 20,
              fontColor: localStorage.getItem('mode') === '0' ? '#000000' : '#ffffff',
              generateLabels: (chart) => {
                const data = chart.data;
                if (data.labels.length && data.datasets.length) {
                  return data.labels.map((label, i) => {
                    const value = data.datasets[0].data[i];
                    const formattedValue = this.getValueFormat(this.dataFormat, value);
                    return {
                      text: `${label}: ${formattedValue}`,
                      fillStyle: data.datasets[0].backgroundColor[i],
                      strokeStyle: data.datasets[0].borderColor,
                      lineWidth: data.datasets[0].borderWidth,
                      hidden: false,
                      index: i
                    };
                  });
                }
                return [];
              }
            }
          },
          layout: {
            padding: {
              top: 20,
            }
          },
          tooltips: {
            callbacks: {
              label: (tooltipItem, data) => {
                const dataset = data.datasets[tooltipItem.datasetIndex];
                const value = dataset.data[tooltipItem.index];
                const label = data.labels[tooltipItem.index];
                const total = dataset.data.reduce((sum, val) => sum + val, 0);
                const percentage = ((value / total) * 100).toFixed(1);
                return `${label}: ${this.getValueFormat(this.dataFormat, value)} (${percentage}%)`;
              }
            }
          }
        }
      });

      // Force a re-render
      this.$nextTick(() => {
        if (this.chart) {
          this.chart.update();
        }
      });
    }
  },
  beforeDestroy() {
    window.removeEventListener('dashboard-widget-resize', this.onTileResize);
    if (this.chart) {
      this.chart.destroy(); // Prevent memory leaks
    }
  }
}
</script>

<style scoped>
canvas {
  width: 100% !important;
  height: 350px !important;
}
canvas.chart-canvas-fill {
  height: 100% !important;
}
</style>
